/**
 * As notas internas do cliente, no painel do atendimento (059, 25/09/2026).
 *
 * ── Para que serve ────────────────────────────────────────────────────────
 *
 * O que vale sobre o cliente para além de qualquer negócio — "prefere ser
 * chamado depois das 18h" —, escrito por quem atendeu, para quem pegar a
 * conversa depois. A nota da oportunidade guarda o que foi combinado NUMA
 * negociação; esta, o que vale para todas.
 *
 * ── Cada nota abre e fecha ────────────────────────────────────────────────
 *
 * Fechada, mostra quem, quando e o começo do texto: dá para correr o olho em
 * dez notas sem rolar. Aberta, o texto inteiro. Nascem FECHADAS — ao contrário
 * dos blocos do painel —, porque aqui o que ocupa espaço é o texto, e a lista
 * inteira aberta empurraria o resto do painel para longe.
 *
 * ── Editar e excluir (060) ────────────────────────────────────────────────
 *
 * Dentro da nota aberta, e só para quem pode: o autor, ou quem edita cadastro
 * de cliente. Quem pode vem pronto do servidor (`pode_alterar`). A nota
 * corrigida ganha a marca "editada"; excluir pede confirmação ali mesmo, sem
 * abrir janela — a nota já está aberta, e é dela que se fala.
 *
 * ── O campo de escrever abre por um botão (#641) ──────────────────────────
 *
 * Aberto o tempo todo, ele ocupava o painel de quem só queria LER as notas, e
 * era o que mais se via no bloco. Agora é um botão "Nova nota"; o campo abre
 * com "Anotar" e "Cancelar", e fecha depois de qualquer um dos dois.
 */

import { useState, type ReactNode } from 'react';
import type { NotaDoCliente } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, useNotasDoCliente } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { Botao, CampoArea } from '@/shared/ui';

const LIMITE = 2000;

/** Data e hora curtas: a nota é do dia a dia, e o ano quase nunca importa. */
function quando(instante: string): string {
  const data = new Date(instante);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NotasDoCliente({ clienteId }: { clienteId: string }): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const consulta = useNotasDoCliente(clienteId);
  const [escrevendo, definirEscrevendo] = useState(false);
  const [texto, definirTexto] = useState('');
  const [salvando, definirSalvando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const limpo = texto.trim();

  const anotar = (): void => {
    if (!api || limpo === '' || salvando) return;
    definirSalvando(true);
    definirErro(null);
    void api
      .criarNotaDoCliente({
        caminho: { id: clienteId },
        corpo: { texto: limpo },
        /*
         * Chave por TENTATIVA: a nota não se apaga por rota nenhuma, então a
         * duplicata do segundo clique ficaria no painel para sempre.
         */
        idempotencyKey: globalThis.crypto?.randomUUID?.() ?? `nota-${Date.now()}-${Math.random()}`,
      })
      .then(() => {
        definirTexto('');
        definirEscrevendo(false);
        invalidar([chaves.notasDoCliente(clienteId)]);
      })
      .catch((causa: unknown) => {
        // O texto FICA no campo: perder o que foi escrito porque a rede caiu é
        // o pior desfecho de uma nota.
        definirErro(
          recadoDeErro(causa, { generica: 'Não foi possível gravar a nota. Tente de novo.' }),
        );
      })
      .finally(() => definirSalvando(false));
  };

  /*
   * Corrigir e excluir devolvem o recado para a NOTA, e não para o campo de
   * escrever: o erro aparece onde a pessoa estava mexendo.
   */
  const corrigir = async (notaId: string, novo: string): Promise<string | null> => {
    if (!api) return 'Sem sessão.';
    try {
      await api.editarNotaDoCliente({
        caminho: { id: clienteId, notaId },
        corpo: { texto: novo },
      });
      invalidar([chaves.notasDoCliente(clienteId)]);
      return null;
    } catch (causa: unknown) {
      return recadoDeErro(causa, {
        generica: 'Não foi possível salvar a correção. Tente de novo.',
      });
    }
  };

  const excluir = async (notaId: string): Promise<string | null> => {
    if (!api) return 'Sem sessão.';
    try {
      await api.removerNotaDoCliente({ caminho: { id: clienteId, notaId } });
      invalidar([chaves.notasDoCliente(clienteId)]);
      return null;
    } catch (causa: unknown) {
      return recadoDeErro(causa, { generica: 'Não foi possível excluir a nota. Tente de novo.' });
    }
  };

  const itens = consulta.data?.itens ?? [];

  return (
    <div className="notas-cliente">
      {consulta.isPending ? (
        <p className="contexto__nota">Buscando o que já foi anotado…</p>
      ) : consulta.isError ? (
        <p className="contexto__nota" role="alert">
          Não foi possível ler as notas agora. Tente reabrir a conversa.
        </p>
      ) : itens.length === 0 ? (
        <p className="contexto__nota">Nada anotado sobre este cliente.</p>
      ) : (
        <ul className="notas-cliente__lista" aria-label="Notas internas do cliente">
          {itens.map((nota) => (
            <Nota key={nota.id} nota={nota} aoCorrigir={corrigir} aoExcluir={excluir} />
          ))}
        </ul>
      )}

      {escrevendo ? (
        <div className="notas-cliente__escrever">
          {erro !== null && (
            <p className="dialogo__erro" role="alert">
              {erro}
            </p>
          )}
          <CampoArea
            rotulo="Nova nota interna"
            rows={2}
            value={texto}
            maxLength={LIMITE}
            autoFocus
            placeholder="Ex.: prefere ser chamado depois das 18h"
            ajuda="Só a equipe vê."
            onChange={(evento) => definirTexto(evento.target.value)}
          />
          <div className="notas-cliente__acoes">
            <Botao
              miudo
              variante="primario"
              onClick={anotar}
              desabilitado={limpo === '' || salvando || !api}
              motivo={salvando ? 'Gravando a nota…' : 'Escreva algo para anotar.'}
            >
              {salvando ? 'Anotando…' : 'Anotar'}
            </Botao>
            <Botao
              miudo
              desabilitado={salvando}
              motivo="Gravando a nota…"
              onClick={() => {
                definirTexto('');
                definirErro(null);
                definirEscrevendo(false);
              }}
            >
              Cancelar
            </Botao>
          </div>
        </div>
      ) : (
        <div className="notas-cliente__acoes">
          <Botao miudo onClick={() => definirEscrevendo(true)}>
            Nova nota
          </Botao>
        </div>
      )}
    </div>
  );
}

function Nota({
  nota,
  aoCorrigir,
  aoExcluir,
}: {
  nota: NotaDoCliente;
  aoCorrigir: (notaId: string, texto: string) => Promise<string | null>;
  aoExcluir: (notaId: string) => Promise<string | null>;
}): ReactNode {
  const [aberta, definirAberta] = useState(false);
  /** O que está acontecendo dentro da nota aberta: ler, corrigir ou confirmar. */
  const [modo, definirModo] = useState<'ler' | 'corrigir' | 'confirmar'>('ler');
  const [rascunho, definirRascunho] = useState(nota.texto);
  const [ocupada, definirOcupada] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const corpo = `nota-do-cliente-${nota.id}`;
  /*
   * A nota sobrevive à saída de quem a escreveu, e a tela diz isso em vez de
   * mostrar um traço: quem lê precisa saber que houve um autor.
   */
  const autor = nota.autor?.nome ?? (nota.autor ? 'sem nome' : 'autor removido');
  const limpo = rascunho.trim();

  const executar = (acao: () => Promise<string | null>, aoTerminar: () => void): void => {
    definirOcupada(true);
    definirErro(null);
    void acao().then((recado) => {
      definirOcupada(false);
      if (recado) definirErro(recado);
      else aoTerminar();
    });
  };

  return (
    <li className="notas-cliente__item">
      <button
        type="button"
        className="notas-cliente__cabeca"
        aria-expanded={aberta}
        aria-controls={corpo}
        onClick={() => definirAberta((estava) => !estava)}
      >
        <span className="notas-cliente__meta">
          {autor} · {quando(nota.criada_em)}
          {nota.editada_em && (
            // Na cabeça, e não só no corpo: quem corre o olho nas notas
            // fechadas precisa saber qual texto foi reescrito.
            <span
              className="notas-cliente__editada"
              title={`Editada em ${quando(nota.editada_em)}`}
            >
              {' '}
              · editada
            </span>
          )}
        </span>
        {!aberta && <span className="notas-cliente__previa">{nota.texto}</span>}
      </button>

      <div id={corpo} className="notas-cliente__corpo" hidden={!aberta}>
        {modo === 'corrigir' ? (
          <div className="notas-cliente__corrigir">
            <CampoArea
              rotulo="Corrigir a nota"
              rows={3}
              value={rascunho}
              maxLength={LIMITE}
              autoFocus
              onChange={(evento) => definirRascunho(evento.target.value)}
            />
            <div className="notas-cliente__acoes">
              <Botao
                miudo
                variante="primario"
                desabilitado={ocupada || limpo === '' || limpo === nota.texto}
                motivo={
                  ocupada
                    ? 'Salvando…'
                    : limpo === ''
                      ? 'A nota não pode ficar vazia.'
                      : 'O texto não mudou.'
                }
                onClick={() =>
                  executar(
                    () => aoCorrigir(nota.id, limpo),
                    () => definirModo('ler'),
                  )
                }
              >
                Salvar
              </Botao>
              <Botao
                miudo
                onClick={() => {
                  definirRascunho(nota.texto);
                  definirErro(null);
                  definirModo('ler');
                }}
              >
                Cancelar
              </Botao>
            </div>
          </div>
        ) : (
          <p className="notas-cliente__texto">{nota.texto}</p>
        )}

        {modo === 'confirmar' && (
          <div className="notas-cliente__confirmar" role="group" aria-label="Confirmar exclusão">
            <p>Excluir esta nota? Ela some para toda a equipe.</p>
            <div className="notas-cliente__acoes">
              <Botao
                miudo
                variante="perigo"
                desabilitado={ocupada}
                motivo="Excluindo…"
                onClick={() =>
                  executar(
                    () => aoExcluir(nota.id),
                    () => undefined,
                  )
                }
              >
                Excluir
              </Botao>
              <Botao miudo onClick={() => definirModo('ler')}>
                Cancelar
              </Botao>
            </div>
          </div>
        )}

        {erro !== null && (
          <p className="dialogo__erro" role="alert">
            {erro}
          </p>
        )}

        {nota.pode_alterar && modo === 'ler' && (
          <div className="notas-cliente__acoes">
            <Botao
              miudo
              onClick={() => {
                definirRascunho(nota.texto);
                definirModo('corrigir');
              }}
            >
              Editar
            </Botao>
            <Botao miudo onClick={() => definirModo('confirmar')}>
              Excluir
            </Botao>
          </div>
        )}
      </div>
    </li>
  );
}
